import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { toast } from 'sonner';
import {
  Megaphone, Search, Sparkles, Send, Mail, Globe, Phone, Star, Trash2,
  Loader2, MapPin, CheckCircle2, X, Users, TrendingUp, Ban, AlertTriangle, FileText, CalendarClock,
  Upload, FileSpreadsheet
} from 'lucide-react';

const API = `${window.location.origin}/api`;

const STATUS_STYLES = {
  new: 'bg-slate-100 text-slate-600',
  contacted: 'bg-blue-100 text-blue-700',
  signed_up: 'bg-emerald-100 text-emerald-700',
  unsubscribed: 'bg-red-100 text-red-600',
  skipped: 'bg-stone-100 text-stone-400',
  failed: 'bg-red-50 text-red-500',
};
const STATUS_LABEL = { new: 'New', contacted: 'Contacted', signed_up: 'Signed up', unsubscribed: 'Unsubscribed', skipped: 'Skipped', failed: 'Failed' };

export default function AdminOutreach() {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [trade, setTrade] = useState('');
  const [location, setLocation] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [selected, setSelected] = useState([]);
  const [compose, setCompose] = useState(null); // lead being composed
  const [busyId, setBusyId] = useState(null);
  const [leadType] = useState('provider');
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [quota, setQuota] = useState(null);
  const [daily, setDaily] = useState(null);
  const [runningDaily, setRunningDaily] = useState(false);
  const PAGE_SIZE = 20;
  const fileInputRef = useRef(null);
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [digestRecipients, setDigestRecipients] = useState([]);
  const [sendingTest, setSendingTest] = useState(false);
  const [savingDigest, setSavingDigest] = useState(false);

  const fetchDigest = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/outreach/digest/settings`, { headers });
      setDigestEnabled(r.data.enabled);
      setDigestRecipients(r.data.recipients || []);
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = useCallback(async () => {
    try { const r = await axios.get(`${API}/outreach/stats`, { headers, params: { lead_type: leadType } }); setStats(r.data); } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadType]);

  const fetchLeads = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/outreach/leads`, { headers, params: { status: statusFilter, q: search, lead_type: leadType, page, page_size: PAGE_SIZE } });
      setLeads(r.data.leads || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, leadType, page]);

  const fetchQuota = useCallback(async () => {
    try { const r = await axios.get(`${API}/outreach/email-quota`, { headers }); setQuota(r.data); } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDaily = useCallback(async () => {
    try { const r = await axios.get(`${API}/outreach/daily-status`, { headers }); setDaily(r.data); } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { setPage(1); }, [statusFilter, search]);
  useEffect(() => { fetchStats(); fetchQuota(); fetchDaily(); }, [fetchStats, fetchQuota, fetchDaily]);
  useEffect(() => { fetchDigest(); }, [fetchDigest]);
  useEffect(() => { const t = setTimeout(fetchLeads, 250); return () => clearTimeout(t); }, [fetchLeads]);

  const toggleDigest = async () => {
    const next = !digestEnabled;
    setSavingDigest(true);
    setDigestEnabled(next);
    try {
      await axios.put(`${API}/outreach/digest/settings`, { enabled: next }, { headers });
      toast.success(next ? 'Weekly digest enabled' : 'Weekly digest paused');
    } catch (e) {
      setDigestEnabled(!next);
      toast.error('Could not update digest setting');
    } finally { setSavingDigest(false); }
  };

  const sendTestDigest = async () => {
    setSendingTest(true);
    try {
      const r = await axios.post(`${API}/outreach/digest/send-test`, {}, { headers });
      toast.success(`Test digest sent to ${r.data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send test digest');
    } finally { setSendingTest(false); }
  };

  const runDailyNow = async () => {
    setRunningDaily(true);
    try {
      const r = await axios.post(`${API}/outreach/run-daily`, {}, { headers });
      toast.success(r.data.message || "Today's batch started");
      setTimeout(() => { fetchDaily(); fetchStats(); fetchLeads(); }, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start daily outreach');
    } finally { setRunningDaily(false); }
  };

  const discover = async () => {
    if (!trade.trim() || !location.trim()) { toast.error('Enter a trade and a location'); return; }
    setDiscovering(true);
    try {
      const r = await axios.post(`${API}/outreach/discover`, { trade, location, limit: 15, lead_type: leadType }, { headers });
      toast.success(`Found ${r.data.found} businesses — ${r.data.new} new leads added`);
      fetchLeads(); fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Discovery failed');
    } finally { setDiscovering(false); }
  };

  const findEmail = async (lead) => {
    setBusyId(lead.id);
    try {
      const r = await axios.post(`${API}/outreach/leads/${lead.id}/find-email`, {}, { headers });
      if (r.data.email) { toast.success(`Found: ${r.data.email}`); fetchLeads(); }
      else toast.info('No public email found on the website');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not search website'); }
    finally { setBusyId(null); }
  };

  const skipLead = async (lead) => {
    try { await axios.put(`${API}/outreach/leads/${lead.id}`, { status: 'skipped' }, { headers }); fetchLeads(); fetchStats(); } catch (e) { /* ignore */ }
  };

  const deleteLead = async (lead) => {
    try { await axios.delete(`${API}/outreach/leads/${lead.id}`, { headers }); fetchLeads(); fetchStats(); setSelected(s => s.filter(x => x !== lead.id)); } catch (e) { /* ignore */ }
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/outreach/export`, { headers, params: { lead_type: leadType }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `outreach-${leadType}.csv`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/outreach/template`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'handybayani-outreach-template.csv'; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download template'); }
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/outreach/import`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      const d = r.data;
      toast.success(`Imported ${d.imported} merchant(s)${d.duplicates ? `, ${d.duplicates} duplicate(s) skipped` : ''}${d.invalid ? `, ${d.invalid} invalid` : ''}`);
      fetchLeads(); fetchStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'CSV import failed');
    } finally { setImporting(false); }
  };

  const bulkSend = async () => {
    const sendable = leads.filter(l => selected.includes(l.id) && l.email && l.status !== 'unsubscribed');
    if (sendable.length === 0) { toast.error('Select leads that have an email address'); return; }
    try {
      const r = await axios.post(`${API}/outreach/bulk-send`, { ids: sendable.map(l => l.id) }, { headers });
      toast.success(`Sent ${r.data.sent} invitation(s), skipped ${r.data.skipped}`);
      setSelected([]); fetchLeads(); fetchStats(); fetchQuota();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk send failed'); }
  };

  const bulkSkip = async () => {
    if (selected.length === 0) return;
    try {
      const r = await axios.post(`${API}/outreach/bulk-skip`, { ids: selected }, { headers });
      toast.success(`Skipped ${r.data.updated} lead(s)`);
      setSelected([]); fetchLeads(); fetchStats();
    } catch (e) { toast.error('Could not skip selected'); }
  };

  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} selected lead(s)? This cannot be undone.`)) return;
    try {
      const r = await axios.post(`${API}/outreach/bulk-delete`, { ids: selected }, { headers });
      toast.success(`Deleted ${r.data.deleted} lead(s)`);
      setSelected([]); fetchLeads(); fetchStats();
    } catch (e) { toast.error('Could not delete selected'); }
  };

  const allOnPageSelected = leads.length > 0 && leads.every(l => selected.includes(l.id));
  const toggleSelectAll = () => {
    const ids = leads.map(l => l.id);
    setSelected(s => allOnPageSelected ? s.filter(x => !ids.includes(x)) : Array.from(new Set([...s, ...ids])));
  };

  const StatCard = ({ icon: Icon, label, value, color, testId }) => (
    <div className="bg-white rounded-2xl border border-stone-200 p-4 flex items-center gap-3" data-testid={testId}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      <div><p className="text-2xl font-bold text-slate-900 leading-tight">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />
      <main className="flex-1 min-w-0 md:ml-64 pt-16 md:pt-6 p-4 md:p-6 overflow-x-hidden">
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-slate-900 font-[Poppins] flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-amber-500" /> Outreach
          </h1>
          <p className="text-sm text-slate-500">Discover local skilled workers &amp; establishments — or import your own list — and invite them to join HandyBayani.</p>
          <div className="flex items-center justify-end gap-2 mt-3 flex-wrap">
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={onFileChosen} data-testid="csv-file-input" />
            <button onClick={downloadTemplate} className="h-9 px-3 rounded-xl bg-white border border-stone-200 text-slate-700 text-sm font-medium hover:bg-stone-100 flex items-center gap-2" data-testid="download-template-btn">
              <FileSpreadsheet className="w-4 h-4" /> CSV template
            </button>
            <button onClick={() => fileInputRef.current?.click()} disabled={importing} className="h-9 px-3 rounded-xl bg-white border border-stone-200 text-slate-700 text-sm font-medium hover:bg-stone-100 flex items-center gap-2 disabled:opacity-50" data-testid="upload-csv-btn">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload CSV
            </button>
            <button onClick={exportCsv} className="h-9 px-3 rounded-xl bg-white border border-stone-200 text-slate-700 text-sm font-medium hover:bg-stone-100 flex items-center gap-2" data-testid="export-csv-btn">
              <FileText className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {stats && !stats.smtp_enabled && (
          <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm" data-testid="smtp-warning">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Email sending is in <strong>preview mode</strong>. Enable SMTP in Admin → Settings to actually send invitations. You can still discover leads and draft invites.</span>
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard icon={Users} label="New leads" value={stats.counts.new} color="bg-slate-100 text-slate-600" testId="stat-new" />
            <StatCard icon={Mail} label="Contacted" value={stats.counts.contacted} color="bg-blue-100 text-blue-600" testId="stat-contacted" />
            <StatCard icon={CheckCircle2} label="Signed up" value={stats.counts.signed_up} color="bg-emerald-100 text-emerald-600" testId="stat-signedup" />
            <StatCard icon={TrendingUp} label="Conversion" value={`${stats.conversion_rate}%`} color="bg-amber-100 text-amber-600" testId="stat-conversion" />
          </div>
        )}

        {/* Daily automated outreach */}
        {daily && (
          <div className="bg-white rounded-2xl border border-stone-200 p-4 mb-4" data-testid="daily-outreach-card">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
                <Send className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900">Daily auto-invites</p>
                <p className="text-xs text-slate-500">
                  Runs every day at 9:00 AM (Manila), sending up to {daily.daily_cap} invites — retrying failed sends first, then new leads, until the whole list is invited.
                </p>
              </div>
              <button
                onClick={runDailyNow}
                disabled={runningDaily || daily.remaining_today === 0 || daily.pending_total === 0}
                className="h-10 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-2 flex-shrink-0"
                data-testid="run-daily-btn"
              >
                {runningDaily ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Run today's batch now
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
              <div className="rounded-xl bg-amber-50 p-2.5 text-center" data-testid="daily-sent">
                <p className="text-lg font-bold text-amber-700">{daily.sent_today}</p>
                <p className="text-[11px] text-slate-500">Sent today</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-2.5 text-center" data-testid="daily-remaining">
                <p className="text-lg font-bold text-slate-700">{daily.remaining_today}</p>
                <p className="text-[11px] text-slate-500">Remaining today</p>
              </div>
              <div className="rounded-xl bg-blue-50 p-2.5 text-center" data-testid="daily-pending">
                <p className="text-lg font-bold text-blue-700">{daily.pending_total}</p>
                <p className="text-[11px] text-slate-500">Queued for invite</p>
              </div>
              <div className="rounded-xl bg-red-50 p-2.5 text-center" data-testid="daily-gaveup">
                <p className="text-lg font-bold text-red-600">{daily.gave_up}</p>
                <p className="text-[11px] text-slate-500">Failed (5+ tries)</p>
              </div>
            </div>
          </div>
        )}

        {/* Weekly digest */}
        <div className="bg-white rounded-2xl border border-stone-200 p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="digest-card">
          <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
            <CalendarClock className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-900">Weekly outreach digest</p>
            <p className="text-xs text-slate-500">Auto-emailed to all admins every Mon 8:00 AM & Fri 5:00 PM (Manila) with leads, invites & sign-ups.{digestRecipients.length > 0 && ` Currently ${digestRecipients.length} recipient(s).`}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={sendTestDigest}
              disabled={sendingTest}
              className="h-9 px-3 rounded-xl bg-white border border-stone-200 text-slate-700 text-sm font-medium hover:bg-stone-100 flex items-center gap-2 disabled:opacity-50"
              data-testid="send-test-digest-btn"
            >
              {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test digest
            </button>
            <button
              onClick={toggleDigest}
              disabled={savingDigest}
              role="switch"
              aria-checked={digestEnabled}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 ${digestEnabled ? 'bg-emerald-500' : 'bg-stone-300'}`}
              data-testid="digest-toggle"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${digestEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        {/* Discover */}
        <div className="bg-white rounded-2xl border border-stone-200 p-4 mb-6">
          <p className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Search className="w-4 h-4 text-amber-500" /> Discover new leads</p>
          <div className="flex flex-col md:flex-row gap-3">
            <input value={trade} onChange={e => setTrade(e.target.value)} placeholder="Trade e.g. electrician, cleaning service" className="flex-1 h-11 px-4 bg-stone-100 rounded-xl text-sm focus:outline-none" data-testid="discover-trade-input" />
            <input value={location} onChange={e => setLocation(e.target.value)} placeholder="Location e.g. Cebu City" className="flex-1 h-11 px-4 bg-stone-100 rounded-xl text-sm focus:outline-none" data-testid="discover-location-input" />
            <button onClick={discover} disabled={discovering} className="h-11 px-5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="discover-btn">
              {discovering ? <><Loader2 className="w-4 h-4 animate-spin" /> Searching…</> : <><Search className="w-4 h-4" /> Discover</>}
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-2">Uses Google Places to find publicly listed local businesses. Nothing is emailed automatically.</p>
        </div>

        {/* Leads */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, trade, location..." className="w-full pl-9 pr-3 h-10 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none" data-testid="leads-search" />
          </div>
          {quota && (
            <div className={`h-10 px-3 rounded-xl border text-xs font-medium flex items-center gap-1.5 ${quota.remaining > 0 ? 'bg-white border-stone-200 text-slate-600' : 'bg-red-50 border-red-200 text-red-600'}`} data-testid="email-quota-badge" title="Combined daily email cap across the whole system; resets each day (Manila)">
              <Mail className="w-3.5 h-3.5" /> Emails today: {quota.used}/{quota.limit}
            </div>
          )}
        </div>

        <div className="flex gap-2 mb-4 flex-wrap items-center">
          {[['', 'All'], ['new', 'New'], ['contacted', 'Contacted'], ['signed_up', 'Signed up'], ['unsubscribed', 'Unsubscribed'], ['skipped', 'Skipped']].map(([val, label]) => (
            <button key={val || 'all'} onClick={() => setStatusFilter(val)} className={`px-3 h-8 rounded-full text-xs font-semibold transition-colors ${statusFilter === val ? 'bg-slate-900 text-white' : 'bg-white border border-stone-200 text-slate-600 hover:bg-stone-100'}`} data-testid={`filter-${val || 'all'}`}>
              {label}
            </button>
          ))}
          <span className="text-xs text-slate-400 ml-auto" data-testid="leads-total">{total} lead(s)</span>
        </div>

        {/* Batch action bar */}
        {leads.length > 0 && (
          <div className="flex items-center gap-2 mb-3 flex-wrap bg-white border border-stone-200 rounded-xl p-2" data-testid="batch-action-bar">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-600 px-2 cursor-pointer">
              <input type="checkbox" checked={allOnPageSelected} onChange={toggleSelectAll} className="w-4 h-4 accent-amber-500" data-testid="select-all-checkbox" />
              Select all on page
            </label>
            {selected.length > 0 ? (
              <>
                <span className="text-xs font-semibold text-slate-700" data-testid="selected-count">{selected.length} selected</span>
                <button onClick={bulkSend} className="h-8 px-3 rounded-lg bg-amber-500 text-slate-900 text-xs font-semibold hover:bg-amber-600 flex items-center gap-1.5" data-testid="bulk-send-btn">
                  <Send className="w-3.5 h-3.5" /> Send invites
                </button>
                <button onClick={bulkSkip} className="h-8 px-3 rounded-lg bg-stone-100 text-slate-600 text-xs font-semibold hover:bg-stone-200 flex items-center gap-1.5" data-testid="bulk-skip-btn">
                  <Ban className="w-3.5 h-3.5" /> Skip
                </button>
                <button onClick={bulkDelete} className="h-8 px-3 rounded-lg bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 flex items-center gap-1.5" data-testid="bulk-delete-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
                <button onClick={() => setSelected([])} className="h-8 px-3 rounded-lg text-slate-500 text-xs font-medium hover:bg-stone-100" data-testid="clear-selection-btn">Clear</button>
              </>
            ) : (
              <span className="text-xs text-slate-400">Select leads to run batch actions</span>
            )}
          </div>
        )}

        <div className="space-y-2" data-testid="leads-list">
          {leads.length === 0 ? (
            <div className="text-center py-16 text-slate-400 bg-white rounded-2xl border border-stone-200">
              <Megaphone className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No leads yet. Discover some above to get started.</p>
            </div>
          ) : leads.map((lead) => (
            <div key={lead.id} className="bg-white rounded-2xl border border-stone-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`lead-${lead.id}`}>
              <input type="checkbox" checked={selected.includes(lead.id)} onChange={() => toggleSelect(lead.id)} className="w-4 h-4 accent-amber-500 flex-shrink-0" data-testid={`lead-check-${lead.id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-900 truncate">{lead.name || 'Unnamed business'}</p>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${STATUS_STYLES[lead.status]}`} data-testid={`lead-status-${lead.id}`}>{STATUS_LABEL[lead.status]}</span>
                  {lead.rating != null && <span className="inline-flex items-center gap-0.5 text-[11px] text-amber-600"><Star className="w-3 h-3 fill-amber-500 text-amber-500" />{lead.rating}</span>}
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5"><MapPin className="w-3 h-3 inline mr-1" />{lead.address || '—'}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                  <span className="capitalize">{lead.trade} · {lead.location}</span>
                  {lead.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
                  {lead.website && <a href={lead.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline"><Globe className="w-3 h-3" />Website</a>}
                  {lead.email
                    ? <span className="inline-flex items-center gap-1 text-emerald-600"><Mail className="w-3 h-3" />{lead.email}</span>
                    : <span className="inline-flex items-center gap-1 text-slate-400"><Mail className="w-3 h-3" />no email</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                {!lead.email && lead.website && (
                  <button onClick={() => findEmail(lead)} disabled={busyId === lead.id} className="h-8 px-2.5 rounded-lg bg-stone-100 text-slate-700 text-xs font-medium hover:bg-stone-200 flex items-center gap-1" data-testid={`find-email-${lead.id}`}>
                    {busyId === lead.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Find email
                  </button>
                )}
                <button onClick={() => setCompose(lead)} className="h-8 px-2.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 flex items-center gap-1" data-testid={`compose-${lead.id}`}>
                  <Send className="w-3.5 h-3.5" /> Invite
                </button>
                {lead.status !== 'skipped' && (
                  <button onClick={() => skipLead(lead)} title="Skip" className="h-8 w-8 rounded-lg bg-stone-100 text-slate-500 hover:bg-stone-200 flex items-center justify-center" data-testid={`skip-${lead.id}`}><Ban className="w-3.5 h-3.5" /></button>
                )}
                <button onClick={() => deleteLead(lead)} title="Delete" className="h-8 w-8 rounded-lg bg-stone-100 text-slate-500 hover:text-red-500 hover:bg-red-50 flex items-center justify-center" data-testid={`delete-${lead.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4" data-testid="pagination">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="h-9 px-3 rounded-lg bg-white border border-stone-200 text-sm text-slate-600 hover:bg-stone-100 disabled:opacity-40" data-testid="page-prev">Prev</button>
            <span className="text-sm text-slate-600" data-testid="page-indicator">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="h-9 px-3 rounded-lg bg-white border border-stone-200 text-sm text-slate-600 hover:bg-stone-100 disabled:opacity-40" data-testid="page-next">Next</button>
          </div>
        )}
      </main>

      {compose && (
        <ComposeModal
          lead={compose}
          headers={headers}
          smtpEnabled={stats?.smtp_enabled}
          onClose={() => setCompose(null)}
          onSent={() => { setCompose(null); fetchLeads(); fetchStats(); }}
          onUpdated={fetchLeads}
        />
      )}
    </div>
  );
}

function ComposeModal({ lead, headers, smtpEnabled, onClose, onSent, onUpdated }) {
  const [email, setEmail] = useState(lead.email || '');
  const [subject, setSubject] = useState(lead.email_subject || '');
  const [body, setBody] = useState(lead.email_body || '');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/outreach/leads/${lead.id}/generate-email`, {}, { headers });
      setSubject(r.data.email_subject); setBody(r.data.email_body);
      toast.success('AI draft ready — review and edit before sending');
      onUpdated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not generate'); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    try { await axios.put(`${API}/outreach/leads/${lead.id}`, { email, email_subject: subject, email_body: body }, { headers }); } catch (e) { /* ignore */ }
  };

  const send = async () => {
    if (!email.trim()) { toast.error('Add an email address first'); return; }
    if (!subject.trim() || !body.trim()) { toast.error('Add a subject and message (try "Write with AI")'); return; }
    setSending(true);
    try {
      await save();
      await axios.post(`${API}/outreach/leads/${lead.id}/send`, {}, { headers });
      toast.success(`Invitation sent to ${email}`);
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send'); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" data-testid="compose-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Send className="w-5 h-5 text-amber-500" /> Invite {lead.name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="compose-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Recipient email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="name@business.com" className="mt-1 w-full h-11 px-3 bg-stone-100 rounded-xl text-sm focus:outline-none" data-testid="compose-email" />
          </div>
          <button onClick={generate} disabled={generating} className="w-full h-11 rounded-xl border-2 border-dashed border-amber-300 text-amber-600 hover:bg-amber-50 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="generate-ai-btn">
            {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Writing…</> : <><Sparkles className="w-4 h-4" /> Write with AI</>}
          </button>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject line" className="mt-1 w-full h-11 px-3 bg-stone-100 rounded-xl text-sm focus:outline-none" data-testid="compose-subject" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Message (HTML)</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={7} placeholder="Your invitation message..." className="mt-1 w-full p-3 bg-stone-100 rounded-xl text-sm focus:outline-none resize-none" data-testid="compose-body" />
            <p className="text-[11px] text-slate-400 mt-1">A signup button, unsubscribe link and footer are added automatically.</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end px-5 py-4 border-t border-stone-200">
          <button onClick={onClose} className="px-4 h-10 rounded-xl text-slate-600 hover:bg-stone-100 text-sm font-medium">Cancel</button>
          <button onClick={send} disabled={sending} className="px-4 h-10 rounded-xl bg-amber-500 text-slate-900 text-sm font-semibold hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2" data-testid="compose-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {smtpEnabled ? 'Send invitation' : 'Send (enable SMTP)'}
          </button>
        </div>
      </div>
    </div>
  );
}
